import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Section } from '../shared';

// Hexagon Integration Card
const HexCard = ({ partner, index, isActive, onClick, size = 'normal' }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const sizeClasses = {
    normal: 'w-28 h-32',
    large: 'w-36 h-40',
  };

  return (
    <motion.div
      className={`relative ${sizeClasses[size]} cursor-pointer`}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.06, type: "spring", stiffness: 200 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={onClick}
    >
      {/* Hexagon Shape */}
      <svg 
        viewBox="0 0 100 115" 
        className="w-full h-full absolute inset-0"
      >
        <defs>
          <linearGradient id={`hexGrad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={isActive || isHovered ? "#FD802E" : "#1a2f3a"} stopOpacity="0.3" />
            <stop offset="100%" stopColor={isActive || isHovered ? "#FD802E" : "#233D4C"} stopOpacity="0.8" />
          </linearGradient>
          <filter id={`glow-${index}`}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <motion.polygon
          points="50,2 95,28 95,87 50,113 5,87 5,28"
          fill={`url(#hexGrad-${index})`}
          stroke={isActive || isHovered ? "#FD802E" : "rgba(253,128,46,0.2)"}
          strokeWidth={isActive || isHovered ? "3" : "1.5"}
          filter={isActive || isHovered ? `url(#glow-${index})` : "none"}
          animate={{
            scale: isHovered ? 1.05 : 1,
          }}
          style={{ transformOrigin: 'center' }}
        />
      </svg>
      
      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
        <motion.div 
          className="w-10 h-10 mb-1 flex items-center justify-center"
          animate={{ scale: isHovered ? 1.2 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <img 
            src={partner.logo} 
            alt={partner.name}
            className="w-full h-full object-contain"
            style={{ 
              filter: isActive || isHovered 
                ? 'invert(1) brightness(1.5) sepia(1) hue-rotate(-30deg) saturate(5)' 
                : 'invert(1) brightness(0.9)' 
            }}
          />
        </motion.div>
        <span className={`text-xs font-medium text-center px-2 transition-colors ${
          isActive || isHovered ? 'text-neon-orange' : 'text-gray-300'
        }`}>
          {partner.name}
        </span>
      </div>
      
      {/* Glow ring on active */}
      {isActive && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="absolute inset-2 rounded-full bg-neon-orange/10 blur-xl" />
        </motion.div>
      )}
    </motion.div>
  );
};

// Category Label Pill
const CategoryPill = ({ category, icon, count, isActive, onClick }) => {
  return (
    <motion.button
      className={`relative px-6 py-3 rounded-2xl font-medium text-sm flex items-center gap-3 transition-all duration-300 ${
        isActive 
          ? 'bg-neon-orange text-dark-base shadow-[0_0_30px_rgba(253,128,46,0.4)]' 
          : 'bg-dark-deeper/80 text-gray-300 border border-neon-orange/20 hover:border-neon-orange/50'
      }`}
      onClick={onClick}
      whileHover={{ scale: 1.05, y: -2 }}
      whileTap={{ scale: 0.95 }}
    >
      <span className="text-xl">{icon}</span>
      <span className="font-heading">{category}</span>
      <span className={`text-xs px-2 py-0.5 rounded-full ${
        isActive ? 'bg-dark-base/30' : 'bg-neon-orange/20 text-neon-orange'
      }`}>
        {count}
      </span>
    </motion.button>
  );
};

// Floating Connection Lines
const ConnectionLines = () => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <svg className="w-full h-full opacity-20">
        <defs>
          <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#FD802E" stopOpacity="0" />
            <stop offset="50%" stopColor="#FD802E" stopOpacity="1" />
            <stop offset="100%" stopColor="#FD802E" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Animated diagonal lines */}
        {[...Array(6)].map((_, i) => (
          <motion.line
            key={i}
            x1={`${-10 + i * 25}%`}
            y1="0%"
            x2={`${30 + i * 25}%`}
            y2="100%"
            stroke="url(#lineGrad)"
            strokeWidth="1"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ 
              pathLength: [0, 1, 0],
              opacity: [0, 0.5, 0],
            }}
            transition={{ 
              duration: 4,
              delay: i * 0.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        ))}
      </svg>
    </div>
  );
};

// Featured Integration Spotlight
const Spotlight = ({ partner, onClose }) => {
  if (!partner) return null;
  
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-dark-base/90 backdrop-blur-md"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-dark-deeper border-2 border-neon-orange rounded-3xl p-8 max-w-md mx-4 text-center"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-neon-orange/10 border border-neon-orange/30 flex items-center justify-center p-4">
          <img 
            src={partner.logo} 
            alt={partner.name}
            className="w-full h-full object-contain"
            style={{ filter: 'invert(1) brightness(1.5) sepia(1) hue-rotate(-30deg) saturate(5)' }}
          />
        </div>
        <h3 className="text-2xl font-heading font-bold text-white mb-2">{partner.name}</h3>
        <p className="text-neon-orange font-medium mb-4">{partner.category}</p>
        <p className="text-gray-400 mb-6">{partner.benefit}</p>
        <button
          className="px-6 py-3 bg-neon-orange text-dark-base font-bold rounded-full"
          onClick={onClose}
        >
          Got it
        </button>
      </motion.div>
    </motion.div>
  );
};

const IntegrationsSection = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const allPartners = [
    // DAWs
    { name: "Pro Tools", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/protools.svg", benefit: "Direct project import & export with full session compatibility", category: "DAWs" },
    { name: "Ableton", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/abletonlive.svg", benefit: "Real-time audio processing and live performance sync", category: "DAWs" },
    { name: "Logic Pro", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/apple.svg", benefit: "Native macOS integration with seamless workflow", category: "DAWs" },
    { name: "Audition", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/adobeaudition.svg", benefit: "Batch processing and Adobe CC integration", category: "DAWs" },
    { name: "Audacity", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/audacity.svg", benefit: "Enhanced free tool with AI capabilities", category: "DAWs" },
    // Streaming
    { name: "Spotify", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/spotify.svg", benefit: "Audio mastering optimized for streaming quality", category: "Streaming" },
    { name: "YouTube", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/youtube.svg", benefit: "Auto-sync video audio with loudness normalization", category: "Streaming" },
    { name: "SoundCloud", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/soundcloud.svg", benefit: "One-click uploads with metadata handling", category: "Streaming" },
    { name: "Apple Music", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/applemusic.svg", benefit: "Lossless and Dolby Atmos audio support", category: "Streaming" },
    { name: "Tidal", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/tidal.svg", benefit: "Master quality audio delivery pipeline", category: "Streaming" },
    // Cloud
    { name: "Google Drive", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/googledrive.svg", benefit: "Auto-sync projects across all devices", category: "Cloud" },
    { name: "Dropbox", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/dropbox.svg", benefit: "Team workspace with version control", category: "Cloud" },
    { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/amazonaws.svg", benefit: "Enterprise-scale processing power", category: "Cloud" },
    { name: "OneDrive", logo: "https://cdn.jsdelivr.net/gh/simple-icons/simple-icons/icons/microsoftonedrive.svg", benefit: "Microsoft 365 and Teams integration", category: "Cloud" },
  ];

  const categories = [
    { name: 'All', icon: '🌐', count: allPartners.length },
    { name: 'DAWs', icon: '🎹', count: allPartners.filter(p => p.category === 'DAWs').length },
    { name: 'Streaming', icon: '📡', count: allPartners.filter(p => p.category === 'Streaming').length },
    { name: 'Cloud', icon: '☁️', count: allPartners.filter(p => p.category === 'Cloud').length },
  ];

  const filteredPartners = activeCategory === 'All' 
    ? allPartners 
    : allPartners.filter(p => p.category === activeCategory);

  return (
    <Section id="integrations" className="bg-dark-base relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <ConnectionLines />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-neon-orange/20 to-transparent border border-neon-orange/30 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            <motion.span 
              className="w-2 h-2 rounded-full bg-neon-orange"
              animate={{ scale: [1, 1.3, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <span className="text-neon-orange font-medium text-sm">Powerful Ecosystem</span>
          </motion.div>

          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
          >
            One Hub,{' '}
            <span className="relative">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange via-yellow-400 to-neon-orange">
                Infinite Connections
              </span>
              <motion.span 
                className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-neon-orange to-transparent"
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ delay: 0.5, duration: 0.8 }}
              />
            </span>
          </motion.h2>

          <motion.p
            className="text-gray-400 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.2 }}
          >
            Tap any integration to discover how VeritoneX connects your tools
          </motion.p>
        </div>

        {/* Category Pills */}
        <motion.div
          className="flex flex-wrap justify-center gap-3 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3 }}
        >
          {categories.map((cat) => (
            <CategoryPill
              key={cat.name}
              category={cat.name}
              icon={cat.icon}
              count={cat.count}
              isActive={activeCategory === cat.name}
              onClick={() => setActiveCategory(cat.name)}
            />
          ))}
        </motion.div>

        {/* Honeycomb Grid */}
        <motion.div
          className="flex flex-wrap justify-center items-center gap-x-[-8px] gap-y-2"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.4 }}
        >
          {filteredPartners.map((partner, index) => {
            // Create honeycomb offset pattern
            const row = Math.floor(index / 5);
            const isOffsetRow = row % 2 === 1;
            
            return (
              <motion.div
                key={partner.name}
                style={{
                  marginLeft: isOffsetRow && index % 5 === 0 ? '64px' : '-8px',
                }}
                layout
              >
                <HexCard
                  partner={partner}
                  index={index}
                  isActive={selectedPartner?.name === partner.name}
                  onClick={() => setSelectedPartner(partner)}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
        >
          {[
            { value: '50+', label: 'Integrations', icon: '/Ecosystem.webp' },
            { value: '2M+', label: 'Connected Users', icon: '/Ecosystem%20(2).webp' },
            { value: '99.9%', label: 'Uptime', icon: '/Ecosystem%20(3).webp' },
            { value: '<100ms', label: 'Sync Speed', icon: '/Ecosystem%20(4).webp' },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              className="text-center p-6 rounded-2xl bg-dark-deeper/50 border border-neon-orange/10 hover:border-neon-orange/30 transition-colors"
              whileHover={{ y: -4 }}
            >
              {typeof stat.icon === 'string' && /\.(webp|png|jpe?g|svg)$/.test(stat.icon) ? (
                <img src={stat.icon} alt={stat.label} className="w-14 h-14 mx-auto mb-3 object-contain" />
              ) : (
                <span className="text-2xl block mb-2">{stat.icon}</span>
              )}
              <div className="text-3xl font-heading font-bold text-neon-orange">{stat.value}</div>
              <div className="text-gray-500 text-sm">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        
      </div>

      {/* Spotlight Modal */}
      {selectedPartner && (
        <Spotlight partner={selectedPartner} onClose={() => setSelectedPartner(null)} />
      )}
    </Section>
  );
};

export default IntegrationsSection;
